"use client";

import { IBrandReviewsSummary } from "@faire/web-api--source/indigofair/data/IBrandReviewsSummary";
import { IGetProductBrandReviewsSummaryResponse } from "@faire/web-api--source/indigofair/data/IGetProductBrandReviewsSummaryResponse";
import { localNum } from "@faire/web/common/localization";

interface IReviewSummaryInputs {
  numberOfReviews: number | undefined;
  overallRating: number | undefined;
  ratingDistribution: Partial<Record<number, number>> | undefined;
  ratingTotals: Partial<Record<number, number>> | undefined;
  metricSummaries: IBrandReviewsSummary.IMetricSummary[] | undefined;
}

export interface IReviewSummary {
  overallRating: number;
  overallRatingText: string;
  numberOfReviews: number;
  reviewDistribution: number[];
  reviewDistributionTotals: number[];
  metricSummaries: IBrandReviewsSummary.IMetricSummary[];
}

/**
 * These function are used to build either brand or product review summary by
 * brandAndProductReviewSummary which has both product and brand review summary.
 */

export const buildBrandReviewSummaryFromProductBrandReview = (
  brandAndProductReviewSummary:
    | Pick<
        IGetProductBrandReviewsSummaryResponse,
        | "brand_total_reviews"
        | "brand_overall_rating"
        | "brand_review_percentage_by_rating"
        | "brand_review_count_by_rating"
        | "metric_summaries"
      >
    | undefined
) => {
  if (!brandAndProductReviewSummary) {
    return undefined;
  }

  return buildReviewSummaryBase({
    numberOfReviews: brandAndProductReviewSummary.brand_total_reviews,
    overallRating: brandAndProductReviewSummary.brand_overall_rating,
    ratingDistribution:
      brandAndProductReviewSummary.brand_review_percentage_by_rating,
    ratingTotals: brandAndProductReviewSummary.brand_review_count_by_rating,
    metricSummaries: brandAndProductReviewSummary.metric_summaries,
  });
};
export const buildProductReviewSummaryFromProductBrandReview = (
  brandAndProductReviewSummary:
    | Pick<
        IGetProductBrandReviewsSummaryResponse,
        | "product_total_reviews"
        | "product_overall_rating"
        | "product_review_percentage_by_rating"
        | "product_review_count_by_rating"
        | "metric_summaries"
      >
    | undefined
) => {
  if (!brandAndProductReviewSummary) {
    return undefined;
  }

  return buildReviewSummaryBase({
    numberOfReviews: brandAndProductReviewSummary.product_total_reviews,
    overallRating: brandAndProductReviewSummary.product_overall_rating,
    ratingDistribution:
      brandAndProductReviewSummary.product_review_percentage_by_rating,
    ratingTotals: brandAndProductReviewSummary.product_review_count_by_rating,
    metricSummaries: brandAndProductReviewSummary.metric_summaries,
  });
};

/**
 * This function is used to build brand review summary by IBrandReviewsSummary
 */
export const buildBrandReviewSummaryFromBrandReviews = (
  brandReviewSummary: IBrandReviewsSummary | undefined
): IReviewSummary | undefined => {
  if (!brandReviewSummary) {
    return undefined;
  }

  const {
    number_of_reviews: numberOfReviews,
    average_rating: overallRating,
    review_percentage_by_rating: ratingDistribution,
    review_count_by_rating: ratingTotals,
    metric_summaries: metricSummaries,
  } = brandReviewSummary;

  return buildReviewSummaryBase({
    numberOfReviews,
    overallRating,
    ratingDistribution,
    ratingTotals,
    metricSummaries,
  });
};

const buildReviewSummaryBase = (
  summaryFields: IReviewSummaryInputs
): IReviewSummary | undefined => {
  const {
    numberOfReviews,
    overallRating,
    ratingDistribution = {},
    ratingTotals = {},
    metricSummaries,
  } = summaryFields;

  if (!numberOfReviews) {
    return undefined;
  }

  return {
    overallRating: overallRating ?? 0,
    overallRatingText:
      localNum(overallRating, {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      }) ?? "",
    numberOfReviews: numberOfReviews ?? 0,
    reviewDistribution: Array(5)
      .fill(0)
      .map((_, i) => ratingDistribution[5 - i] ?? 0),
    reviewDistributionTotals: Array(5)
      .fill(0)
      .map((_, i) => ratingTotals[5 - i] ?? 0),
    metricSummaries:
      metricSummaries?.filter((metric) => !!metric.average_rating) ?? [],
  };
};
